import { useState } from "react";
import { TrendingUp, Sparkles } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { PageHeader } from "@/components/PageHeader";
import { useNovexData } from "@/hooks/useNovexData";
import { useLang } from "@/context/LanguageContext";
import { ASSETS } from "@/lib/assets";

const COT_ASSETS = ASSETS.filter((a) => a.cot);

const compact = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(0)}k`;
  return String(v);
};

const AXIS = { fontSize: 10, fill: "#475569" };
const TOOLTIP = { background: "#0B0E14", border: "1px solid #222733", borderRadius: 8, fontSize: 12 };

function ChartCard({ title, subtitle, status, headerExtra, children }) {
  return (
    <div className="rounded-lg border border-[#222733] bg-[#0E1118] p-4 sm:p-5" data-testid={`trend-${title}`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="font-heading text-sm font-bold text-white">{title}</h3>
        {subtitle && <span className="text-[10px] text-slate-600">{subtitle}</span>}
      </div>
      {headerExtra}
      {status === "loading" ? <div className="h-64 animate-pulse rounded bg-slate-800/40" /> : children}
    </div>
  );
}

function CpiChart({ t, fmtDate }) {
  const { status, data } = useNovexData("/history/cpi");
  const pts = data?.points || [];
  return (
    <ChartCard title={t("trends.cpiTitle")} subtitle={t("trends.cpiSub")} status={status}>
      <div className="h-64">
        <ResponsiveContainer width="99%" height="100%">
          <LineChart data={pts} margin={{ top: 5, right: 12, bottom: 0, left: -20 }}>
            <CartesianGrid stroke="#1a1f2b" vertical={false} />
            <XAxis dataKey="date" tickFormatter={fmtDate} tick={AXIS} axisLine={false} tickLine={false} minTickGap={24} />
            <YAxis tick={AXIS} tickFormatter={(v) => `${v}%`} axisLine={false} tickLine={false} width={44} domain={["auto", "auto"]} />
            <Tooltip contentStyle={TOOLTIP} labelFormatter={fmtDate} formatter={(v, n) => [`${v}%`, n === "cpi" ? t("trends.cpiHeadline") : t("trends.cpiCore")]} />
            <Legend formatter={(v) => (v === "cpi" ? t("trends.cpiHeadline") : t("trends.cpiCore"))} wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="cpi" stroke="#F59E0B" strokeWidth={2} dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="core" stroke="#38BDF8" strokeWidth={1.6} strokeDasharray="4 3" dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}

function RateChart({ t, fmtDate }) {
  const { status, data } = useNovexData("/history/rate");
  const pts = data?.points || [];
  return (
    <ChartCard title={t("trends.rateTitle")} subtitle={t("trends.rateSub")} status={status}>
      <div className="h-64">
        <ResponsiveContainer width="99%" height="100%">
          <AreaChart data={pts} margin={{ top: 5, right: 12, bottom: 0, left: -20 }}>
            <defs>
              <linearGradient id="rateFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10B981" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#1a1f2b" vertical={false} />
            <XAxis dataKey="date" tickFormatter={fmtDate} tick={AXIS} axisLine={false} tickLine={false} minTickGap={24} />
            <YAxis tick={AXIS} tickFormatter={(v) => `${v}%`} axisLine={false} tickLine={false} width={44} domain={["auto", "auto"]} />
            <Tooltip contentStyle={TOOLTIP} labelFormatter={fmtDate} formatter={(v) => [`${v}%`, t("trends.rate")]} />
            <Area type="stepAfter" dataKey="rate" stroke="#10B981" strokeWidth={2} fill="url(#rateFill)" dot={false} isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}

function CotChart({ t, fmtDate, loc, lang }) {
  const [market, setMarket] = useState("OR");
  const { status, data } = useNovexData(`/history/cot?market=${encodeURIComponent(market)}`);
  const pts = data?.points || [];
  return (
    <ChartCard title={t("trends.cotTitle")} subtitle={t("trends.cotSub")} status={status}
      headerExtra={
        <div className="mb-3 flex flex-wrap gap-1.5" data-testid="cot-market-selector">
          {COT_ASSETS.map((a) => (
            <button key={a.id} onClick={() => setMarket(a.cot)} data-testid={`cot-market-${a.id}`}
              className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                market === a.cot ? "border-purple-500/60 bg-purple-500/15 text-purple-200" : "border-[#222733] bg-[#11141A] text-slate-400 hover:text-white"
              }`}>
              {a.flag} {lang === "en" && a.labelEn ? a.labelEn : a.label}
            </button>
          ))}
        </div>
      }>
      <div className="h-64">
        <ResponsiveContainer width="99%" height="100%">
          <AreaChart data={pts} margin={{ top: 5, right: 12, bottom: 0, left: -6 }}>
            <defs>
              <linearGradient id="cotFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#A855F7" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#A855F7" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#1a1f2b" vertical={false} />
            <XAxis dataKey="date" tickFormatter={fmtDate} tick={AXIS} axisLine={false} tickLine={false} minTickGap={24} />
            <YAxis tick={AXIS} tickFormatter={compact} axisLine={false} tickLine={false} width={48} domain={["auto", "auto"]} />
            <Tooltip contentStyle={TOOLTIP} labelFormatter={fmtDate} formatter={(v) => [Number(v).toLocaleString(loc), t("c.net")]} />
            <Area type="monotone" dataKey="net" stroke="#A855F7" strokeWidth={2} fill="url(#cotFill)" dot={false} isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}

export default function Trends() {
  const { t, lang } = useLang();
  const loc = lang === "en" ? "en-US" : "fr-FR";
  const fmtDate = (d) => {
    const dt = new Date(d + "T00:00:00");
    return isNaN(dt) ? d : dt.toLocaleDateString(loc, { month: "short", year: "2-digit" });
  };
  return (
    <div className="space-y-6" data-testid="trends-page">
      <PageHeader title={t("trends.title")} subtitle={t("trends.sub")} icon={TrendingUp} />
      <div className="flex items-start gap-2 rounded-lg border border-amber-900/40 bg-amber-950/20 p-3">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
        <p className="text-sm text-amber-100/90">{t("trends.tip")}</p>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <CpiChart t={t} fmtDate={fmtDate} />
        <RateChart t={t} fmtDate={fmtDate} />
      </div>
      <CotChart t={t} fmtDate={fmtDate} loc={loc} lang={lang} />
    </div>
  );
}
